/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  Coordinates,
  SoundingsCollection,
  SourceSelection,
} from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import type { SoundingsProfileDataPreferences } from '../utils/profileDataPreferences';

export enum SoundingsActionOrigin {
  soundingsManager = 'soundingsManager',
  soundingsComponent = 'soundingsComponent',
}

export interface SourceType {
  collectionId: string;
  url: string;
  instanceId?: string;
  verticalRange?: string;
  parameterMappings?: Record<string, string>;
  windSpeedUnit?: string;
  name?: string;
  prefetchAllTimesteps?: boolean;
  /** Grid resolution in km, used for radius queries to find nearby grid points. Default ~2.5km */
  gridResolution?: number;
}

export interface SoundingsPayload {
  viewId?: string;
  origin?: string;
}

export interface UpdateSourcePayload extends SoundingsPayload {
  sourceId: string;
  value: SourceSelection;
}

export interface UpdateLocationPayload extends SoundingsPayload {
  sourceId: string;
  location: string | Coordinates | undefined;
}

export interface UpdateTimePayload extends SoundingsPayload {
  selectedTime: string;
}

export interface UpdateDiagramTypePayload extends SoundingsPayload {
  diagramType: 'SKEWT' | 'BIJVOET' | 'EMAGRAM';
}

export interface ToggleLocationMarkersPayload extends SoundingsPayload {
  shouldHideLocationMarkers: boolean;
}

export interface ToggleTimeSliderVisibilityPayload extends SoundingsPayload {
  isTimeSliderVisible: boolean;
}

export interface UpdateProfileDataPreferencesPayload extends SoundingsPayload {
  profileDataPreferences: SoundingsProfileDataPreferences;
}

export interface RegisterSoundingsPresetPayload extends SoundingsPayload {
  preset: SoundingsViewState;
}

export interface AddSoundingPanelsPayload extends SoundingsPayload {
  panels: SoundingPanels;
}

export interface ChangeSoundingPanelOrderPayload extends SoundingsPayload {
  newOrderOfIds: string[];
}

export type SoundingsRootState = CoreAppStore & SoundingsModuleState;

export interface SoundingPanel {
  id: string;
  name: string;
  isDisplayed: boolean;
  isOpen?: boolean;
  customWidth?: number;
}

export interface SoundingPanels {
  order: string[];
  entities: Record<string, SoundingPanel>;
}

/**
 * State for a single soundings view. Each open soundings view gets its
 * own instance of this state, so multiple views can show different
 * sources, locations, and settings independently.
 */
export interface SoundingsViewState {
  /** key: sourceId */
  selectedSources: Record<string, SourceSelection>;
  /** key: sourceId, value: locationId or position coordinates */
  selectedLocations: Record<string, string | Coordinates | undefined>;
  /** ISO date, e.g., 2025-01-02T12:00:00.000Z */
  selectedTime?: string;
  /** Diagram type: SKEWT, BIJVOET, or EMAGRAM */
  diagramType?: 'SKEWT' | 'BIJVOET' | 'EMAGRAM';
  soundingPanels: SoundingPanels;
  /** Whether to hide location markers on the map */
  shouldHideLocationMarkers?: boolean;
  /** Whether the timeslider is shown below the panels (defaults to true) */
  isTimeSliderVisible?: boolean;
  /** Per-view profile data table preferences: visible columns + units */
  profileDataPreferences?: SoundingsProfileDataPreferences;
  syncGroupsIds: string[];
}

/**
 * Root state for the soundings slice. Views are added dynamically when
 * a soundings view mounts, so the state is organized as a map keyed by
 * viewId. `soundingsCollections` is shared across all views.
 */
export interface SoundingsState {
  /** Each open soundings view's state, keyed by its viewId */
  byId: Record<string, SoundingsViewState>;
  /** Which views are currently active — actions for unknown views are ignored */
  registeredSoundingsViewIds?: string[];
  /** Available sounding data collections, shared across all views */
  soundingsCollections: SoundingsCollection[];
  /** Counter bumped on each view close so soundings panels re-grow into the freed space; transient, never persisted to presets. */
  resizeOnViewCloseSignal?: number;
}

export interface SoundingsModuleState {
  soundings?: SoundingsState;
}
